import React from 'react';
import { Collapse, Tag, Tooltip, Button, Popconfirm } from 'antd';
import { toast } from '../../../utils/toast.js';
import {
  FileTextOutlined,
  CheckCircleFilled,
  PaperClipOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import { lessonApi } from '../../../api/lesson.api.js';
import { ModuleGateTag } from '../../learning/components/ModuleGateTag.jsx';
import { isGateBlocked, gateMessage } from '../../learning/utils/moduleGate.js';

export function SyllabusList({
  syllabus = [],
  courseId,
  courseInstructorId,
  completedLessonIds = [],
  isEnrolled,
  onRefresh,
}) {
  const navigate = useNavigate();
  const { user, isAdmin, isInstructor } = useAuth();
  // Admin xóa được mọi bài; giảng viên chỉ xóa bài trong khóa mình phụ trách (YC-ADM-09)
  const canManageThisCourse = isAdmin || (isInstructor && !!user?.id && user.id === courseInstructorId);

  const formatDuration = (lesson) => {
    if (lesson.estimatedMinutes) {
      return `${lesson.estimatedMinutes} phút`;
    }
    if (lesson.durationSeconds) {
      const mins = Math.floor(lesson.durationSeconds / 60);
      return `${mins} phút`;
    }
    return '40 phút';
  };

  const handleLessonClick = (lesson) => {
    // Module đang xây dựng / chưa đạt test module trước: báo lý do thay vì mở trang lỗi (admin, giảng viên vẫn vào được)
    if (isGateBlocked(lesson.gate) && !isAdmin && !isInstructor) {
      toast.info(gateMessage(lesson.gate));
      return;
    }
    navigate(`/learn/${courseId}/lessons/${lesson.id}`);
  };

  const handleDeleteLesson = async (e, lessonId) => {
    if (e && e.stopPropagation) e.stopPropagation();
    try {
      await lessonApi.deleteLesson(lessonId);
      toast.success('Đã xóa module bài học khỏi khóa đào tạo!');
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      toast.error(err.message || 'Lỗi khi xóa bài học');
    }
  };

  const items = syllabus.map((module, mIdx) => ({
    key: module.id || String(mIdx),
    label: (
      <div className="syllabus-module-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, width: '100%' }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>
          {module.title}
        </span>
        <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
          {module.lessons?.length || 0} bài học chuyên sâu
        </span>
      </div>
    ),
    children: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {module.lessons?.map((lesson, lIdx) => {
          const isCompleted = completedLessonIds.includes(lesson.id);
          // API chi tiết khóa học chỉ trả số lượng, không trả nội dung tài liệu (YC-COURSE-04)
          const attachmentsCount = lesson.attachmentsCount || 0;

          return (
            <div
              key={lesson.id || lIdx}
              className="syllabus-lesson-row"
              onClick={() => handleLessonClick(lesson)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                padding: '12px 16px',
                borderRadius: 8,
                background: isCompleted ? '#f0fdf4' : '#f8fafc',
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: '1px solid',
                borderColor: isCompleted ? '#bbf7d0' : '#e2e8f0',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f0f9ff';
                e.currentTarget.style.borderColor = '#bae6fd';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isCompleted ? '#f0fdf4' : '#f8fafc';
                e.currentTarget.style.borderColor = isCompleted ? '#bbf7d0' : '#e2e8f0';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                {isCompleted ? (
                  <CheckCircleFilled style={{ color: '#10b981', fontSize: 18, flexShrink: 0 }} />
                ) : (
                  <FileTextOutlined style={{ color: '#0284c7', fontSize: 18, flexShrink: 0 }} />
                )}

                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {lesson.code && (
                      <Tag color="blue" style={{ fontWeight: 700, margin: 0, flexShrink: 0 }}>
                        {lesson.code}
                      </Tag>
                    )}
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                      {lesson.title.replace(/^[A-Z0-9]+\s*—\s*/, '')}
                    </span>
                    <ModuleGateTag gate={lesson.gate} />
                  </div>
                </div>
              </div>

              <div
                className="syllabus-lesson-meta"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  fontSize: 12,
                  color: '#64748b',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                {attachmentsCount > 0 && (
                  <Tooltip title={`${attachmentsCount} file công cụ đính kèm`}>
                    <Tag icon={<PaperClipOutlined />} color="cyan" style={{ margin: 0, whiteSpace: 'nowrap' }}>
                      {attachmentsCount} tài liệu
                    </Tag>
                  </Tooltip>
                )}

                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  <ClockCircleOutlined />
                  <span style={{ whiteSpace: 'nowrap' }}>{formatDuration(lesson)}</span>
                </div>

                {/* Nút Xóa Module: Admin hoặc giảng viên phụ trách khóa */}
                {canManageThisCourse && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <Popconfirm
                      title="Xác nhận xóa bài học này?"
                      description={`Bạn có chắc chắn muốn xóa bài học "${lesson.title}" khỏi khóa đào tạo?`}
                      onConfirm={(e) => handleDeleteLesson(e, lesson.id)}
                      okText="Xóa bài học"
                      cancelText="Hủy"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        size="small"
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        title="Xóa module bài học này"
                        style={{ padding: '0 6px', color: '#ef4444' }}
                      />
                    </Popconfirm>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    ),
  }));

  return (
    <Collapse
      className="course-syllabus"
      defaultActiveKey={syllabus.map((m, idx) => m.id || String(idx))}
      items={items}
      style={{ background: '#ffffff', borderRadius: 10, border: '1px solid #e2e8f0' }}
    />
  );
}
